import { ApolloError } from '@apollo/client';
import type { GraphQLFormattedError } from 'graphql';

export const NO_VALID_PASSWORD_PROVIDED = 'No valid password provided';

// Matches the event_service error verbatim. A backend `extensions.code` would be more durable than
// message-matching — tracked as a follow-up; swap to `hasGraphQLErrorCode` once it exists.
export const PHOTO_LIMIT_REACHED_MESSAGE = 'Maximum number of photos reached';

// event_service `_canCreateMediaForEvent` codes: storage expiry blocks host/admin uploads,
// upload-window expiry blocks guest (magic-token) uploads. Independent clocks, distinct remedies.
export const PHOTO_STORAGE_EXPIRED_CODE = 'PHOTO_STORAGE_EXPIRED';
export const PHOTO_UPLOAD_EXPIRED_CODE = 'PHOTO_UPLOAD_EXPIRED';

export const hasAuthError = (err: ApolloError) => {
  return !!err?.graphQLErrors?.find(err => {
    const errCode = err?.extensions?.props?.statusCode;
    return errCode && (errCode === 401 || errCode === 403);
  });
};

/**
 * Checks whether an unknown caught error is an ApolloError containing a GraphQL
 * error with the given extensions code (e.g. 'TICKET_SOLD_OUT').
 * Accepts `unknown` so it can be used directly in try/catch blocks.
 */
export const hasGraphQLErrorCode = (error: unknown, code: string): boolean => {
  if (!(error instanceof ApolloError)) return false;
  return error.graphQLErrors.some(err => err.extensions?.['code'] === code);
};

/**
 * Checks whether an unknown caught error is an ApolloError containing a GraphQL
 * error with the given message.
 * Accepts `unknown` so it can be used directly in try/catch blocks.
 */
export const hasGraphQLErrorMessage = (error: unknown, message: string): boolean => {
  if (!(error instanceof ApolloError)) return false;
  return error.graphQLErrors.some(err => err.message === message);
};

/**
 * Checks whether the error indicates the event photo limit has been reached.
 * Works with both thrown ApolloErrors (catch blocks) and the `errors` array
 * returned from Apollo mutation results.
 */
export const isPhotoLimitError = (error: unknown): boolean => {
  // Thrown ApolloError (catch blocks) — reuse the shared matcher.
  if (hasGraphQLErrorMessage(error, PHOTO_LIMIT_REACHED_MESSAGE)) return true;
  // The `errors` array returned on a mutation result (not an ApolloError).
  if (Array.isArray(error)) {
    return error.some((err: GraphQLFormattedError) => err.message === PHOTO_LIMIT_REACHED_MESSAGE);
  }
  return false;
};

/**
 * Checks whether an unknown caught error carries the given `extensions.code`. Like
 * `isPhotoLimitError`, works with both thrown ApolloErrors (catch blocks) and the `errors`
 * array returned from Apollo mutation results.
 */
const hasErrorCode = (error: unknown, code: string): boolean => {
  if (hasGraphQLErrorCode(error, code)) return true;
  if (Array.isArray(error)) {
    return error.some((err: GraphQLFormattedError) => err.extensions?.['code'] === code);
  }
  return false;
};

/** Host/admin upload rejected because the event's photo storage has expired — remedy is Add storage. */
export const isPhotoStorageExpiredError = (error: unknown): boolean => hasErrorCode(error, PHOTO_STORAGE_EXPIRED_CODE);

/** Guest upload rejected because the guest upload window is closed (lapsed or never included). */
export const isPhotoUploadExpiredError = (error: unknown): boolean => hasErrorCode(error, PHOTO_UPLOAD_EXPIRED_CODE);
